/** Phase, unlocking and holds, section 5. */
import { PLAN } from './data.ts';
import type { PlanState, Gate } from './types.ts';
import { bestMeanUpTo } from './weights.ts';

export function isDeload(week: number): boolean {
  return PLAN.meta.deloadWeeks.includes(week);
}

export function critMet(
  state: PlanState, startDate: string, gate: Gate, index: number,
): boolean {
  const c = gate.crit[index];
  if (c.auto !== undefined) {
    const best = bestMeanUpTo(state, startDate, gate.week);
    return best !== null && best <= c.auto;
  }
  return state.get(`crit:${gate.lvl}:${index}`) === 1;
}

export function gatePassed(state: PlanState, startDate: string, gate: Gate): boolean {
  return gate.crit.every((_, i) => critMet(state, startDate, gate, i));
}

/** Gates are evaluated in order and stop at the first unpassed one. */
export function unlockedLevel(state: PlanState, startDate: string): number {
  let lv = 1;
  for (const g of PLAN.gates) {
    if (gatePassed(state, startDate, g)) lv = g.lvl + 1;
    else break;
  }
  return Math.min(lv, 4);
}

export interface PhaseInfo {
  scheduled: number;   // the phase the calendar says
  eff: number;         // the phase actually served
  unlocked: number;
  hold: boolean;
  held: number;        // weeks spent past the gate
  forced: boolean;     // held more than two weeks: the level opens anyway
}

export function phaseFor(state: PlanState, startDate: string, week: number): PhaseInfo {
  const scheduled = Math.min(Math.max(Math.ceil(week / 4), 1), 4);
  const unlocked = unlockedLevel(state, startDate);
  const held = Math.max(0, week - unlocked * 4);
  const forced = held > 2;
  const effRaw = forced ? Math.min(scheduled, unlocked + 1) : Math.min(scheduled, unlocked);
  const eff = Math.min(Math.max(effRaw, 1), 4);
  return { scheduled, eff, unlocked, hold: eff < scheduled || forced, held, forced };
}

/**
 * Which week's running the user actually gets. A hold serves the last BUILD week of the
 * block, never a repeat of the test week - section 3, non-negotiable.
 */
export function runWeekFor(state: PlanState, startDate: string, week: number): number {
  const p = phaseFor(state, startDate, week);
  if (!p.hold) return Math.min(Math.max(week, 1), 16);
  return Math.max(1, p.eff * 4 - 1);
}

export function gateForWeek(week: number): Gate | undefined {
  return PLAN.gates.find((g) => g.week === week);
}
